import { backendUrl } from "../config_prod";
import { FormLanguage, FormState } from "../type/form-step";

export type ExtractedResume = {
  identity?: {
    firstName?: string | null;
    lastName?: string | null;
    professionalTitle?: string | null;
  };
  contact?: {
    email?: string | null;
    phone?: string | null;
    address?: string | null;
    postalCode?: string | null;
    city?: string | null;
    country?: string | null;
  };
  searchSuggestions?: {
    preferredCity?: string | null;
    contractTypes?: string[];
    remoteWork?: string[];
  };
  education?: { educationLevel?: string | null }[];
  languages?: { language: string | null; level: string | null }[];
  calculatedProfile?: {
    totalExperienceYears?: number | null;
    currentJobTitle?: string | null;
  };
  additionalInformation?: {
    availability?: string | null;
    remoteWorkPreference?: string | null;
    desiredContractTypes?: string[];
    mobilityRadiusKm?: number | null;
    minSalary?: number | null;
    maxSalary?: number | null;
    currency?: string | null;
  };
};

export function mapResumeFields(profile: ExtractedResume): Partial<FormState> {
  const identity = profile.identity || {};
  const contact = profile.contact || {};
  const extra = profile.additionalInformation || {};
  const values: Partial<FormState> = {};
  const strings = {
    firstName: identity.firstName,
    lastName: identity.lastName,
    role:
      identity.professionalTitle || profile.calculatedProfile?.currentJobTitle,
    email: contact.email,
    phone: contact.phone,
    address: [
      contact.address,
      contact.postalCode,
      contact.city,
      contact.country,
    ]
      .filter(Boolean)
      .join(", "),
    city: profile.searchSuggestions?.preferredCity,
  };
  Object.entries(strings).forEach(([key, value]) => {
    if (value) (values as Record<string, unknown>)[key] = value;
  });
  const levels = ["NO_DEGREE", "BAC", "BAC+2_3", "BAC+4_5", "DOCTORAT"];
  const education = (profile.education || [])
    .map((item) => item.educationLevel)
    .filter((level): level is string => !!level && levels.includes(level));
  if (education.length)
    values.schoolLevel = education.sort(
      (a, b) => levels.indexOf(b) - levels.indexOf(a),
    )[0];
  const years = profile.calculatedProfile?.totalExperienceYears;
  if (typeof years === "number" && Number.isFinite(years) && years >= 0) {
    values.experienceMonths =
      years < 2
        ? "-1"
        : years < 3
          ? "24"
          : years < 5
            ? "36"
            : years < 10
              ? "60"
              : "120";
  }
  if (
    extra.availability &&
    ["NOW", "LESS_THAN_1_MONTH", "1_TO_3_MONTHS"].includes(extra.availability)
  )
    values.availableDate = extra.availability;
  const remote = (
    extra.remoteWorkPreference
      ? [extra.remoteWorkPreference]
      : profile.searchSuggestions?.remoteWork || []
  ).filter((value) => ["non", "hybrid", "full"].includes(value));
  if (remote.length) values.workType = remote;
  const contracts = (
    extra.desiredContractTypes?.length
      ? extra.desiredContractTypes
      : profile.searchSuggestions?.contractTypes || []
  ).filter((value) =>
    [
      "cdi",
      "cdd",
      "freelance",
      "interim",
      "stage",
      "alternant",
      "vie",
      "civic_service",
      "help_contract",
      "partial_time",
    ].includes(value),
  );
  if (contracts.length) values.contractType = contracts;
  if (typeof extra.mobilityRadiusKm === "number" && extra.mobilityRadiusKm >= 0)
    values.rayonKm = String(extra.mobilityRadiusKm);
  if (extra.currency && ["EUR", "USD", "GBP", "CHF"].includes(extra.currency)) {
    values.currency = extra.currency;
    if (typeof extra.minSalary === "number" && extra.minSalary >= 0)
      values.minSalary = String(extra.minSalary);
    if (typeof extra.maxSalary === "number" && extra.maxSalary >= 0)
      values.maxSalary = String(extra.maxSalary);
  }
  return values;
}

export async function resolveResumeLanguages(
  profile: ExtractedResume,
  locale: string,
  signal: AbortSignal,
): Promise<FormLanguage[]> {
  const languages = await Promise.all(
    (profile.languages || []).map(async (language) => {
      if (!language.language) return null;
      const response = await fetch(
        `${backendUrl}/kscan/v1/languages/get/autocomplete?lang=${locale}&q=${encodeURIComponent(language.language)}&top=7`,
        { signal },
      );
      if (!response.ok) return null;
      const result = await response.json();
      const match = result.data?.find((item: FormLanguage) =>
        [item.code, item.name_fr, item.name_en].some(
          (name) => name.toLowerCase() === language.language!.toLowerCase(),
        ),
      );
      return match
        ? ({
            ...match,
            level:
              language.level &&
              ["native", "fluent", "intermediate", "basic"].includes(
                language.level,
              )
                ? language.level
                : "",
          } as FormLanguage)
        : null;
    }),
  );
  return languages.filter(
    (language, index): language is FormLanguage =>
      !!language &&
      languages.findIndex((item) => item?.code === language.code) === index,
  );
}
